"use client"

import { useState } from "react"
import AddStoryModal from "../../modals/AddStoryModal"
import ShowStoryModal from "../../modals/ShowStoryModal"
import { storiesAPI } from "../../../services/api"
import { useAuth } from "../../../hooks/useAuth"
import { getUserPlaceholderImage } from "../../../utils/imageUtils"

const StoriesCarousel = ({ stories, currentUser, onStoryAdded, onStoryDeleted, viewedStoryIds = new Set(), onStoryViewed }) => {
  const { getUserType } = useAuth();

  // Get user type color
  const currentUserType = getUserType();
  const getUserTypeColor = (userType) => {
    const userTypeConfig = {
      'PET_GUARDIAN': '#8548A8', // Purple
      'VETERINARIAN': '#00D4B3', // Cyan
      'SERVICE_PROVIDER': '#D65745', // Orange
      'CLINIC_MODERATOR': '#2A8AD2', // Blue
    };
    return userTypeConfig[userType] || '#8548A8'; // Default to purple
  };
  const userTypeColor = getUserTypeColor(currentUserType);

  const [isAddStoryModalOpen, setIsAddStoryModalOpen] = useState(false)
  const [isShowStoryModalOpen, setIsShowStoryModalOpen] = useState(false)
  const [selectedStoryIndex, setSelectedStoryIndex] = useState(0)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)

  const handleStoryClick = (story, index) => {
    if (story.user?.isAddStory) {
      setIsAddStoryModalOpen(true)
    } else {
      setSelectedStoryIndex(index)
      setIsShowStoryModalOpen(true)
    }
  }

  const handleAddStory = async (storyData) => {
    setIsUploading(true)
    setUploadProgress(0)
    try {
      const result = await storiesAPI.createStory(storyData, (progress) => {
        setUploadProgress(progress)
      })

      // Notify parent component to refresh stories
      if (onStoryAdded) {
        onStoryAdded()
      }

      setIsAddStoryModalOpen(false)
    } catch (error) {
      console.error('Error uploading story:', error)
      throw error // Re-throw to let the modal handle the error
    } finally {
      setIsUploading(false)
    }
  }

  const handleCloseAddModal = () => {
    setIsAddStoryModalOpen(false)
  }

  const handleCloseShowModal = () => {
    setIsShowStoryModalOpen(false)
  }

  return (
    <div
      style={{
        background: "transparent",
        width: "100%",
      }}
    >
      <div
        style={{
          maxWidth: "100%",
          // marginTop: "5vh",
          padding: "10px",
          overflow: "hidden",
          position: "relative",
          boxSizing: "border-box",
        }}
        className="stories-container"
      >
        <div
          className="stories-scroll-container"
          style={{
            display: "flex",
            alignItems: "center",
            overflowX: "auto",
            overflowY: "hidden",
            paddingBottom: "0px",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            justifyContent: "flex-start",
            scrollBehavior: "smooth",
            WebkitOverflowScrolling: "touch",
            scrollSnapType: "x mandatory",
            scrollPaddingLeft: "60px",
          }}
        >
          {stories.map((story, index) => {
            // Determine if story group has unviewed stories
            // Check if it's a grouped story (has stories array) or single story
            // Primary check: backend has_viewed field, secondary: viewedStoryIds Set
            const hasUnviewedStories = story.user?.isAddStory 
              ? false 
              : story.stories && story.stories.length > 0
                ? story.stories.some(s => {
                    // Use storyId (the actual backend story ID) not the composite id
                    const storyId = s.storyId || s.id;
                    const storyIdStr = String(storyId);
                    
                    // Check backend has_viewed first, then fallback to Set
                    const isViewed = s.has_viewed || viewedStoryIds.has(storyIdStr);
                    return !isViewed;
                  })
                : // Single story (no stories array)
                  (() => {
                    const storyId = story.storyId || story.id;
                    const storyIdStr = String(storyId);
                    const isViewed = story.has_viewed || viewedStoryIds.has(storyIdStr);
                    return !isViewed;
                  })();
            
            // Use gray border for viewed stories, colored for unviewed
            const borderColor = story.user?.isAddStory
              ? "#d1d5db"
              : hasUnviewedStories
                ? userTypeColor
                : "#9CA3AF"; // Gray for viewed
            const borderWidth = story.user?.isAddStory
              ? "3px"
              : hasUnviewedStories
                ? "4px"
                : "3px";
            const borderStyle = story.user?.isAddStory ? "dashed" : "solid";

            return (
            <div
              key={story.id}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                textAlign: "center",
                cursor: "pointer",
                maxWidth: "110px",
                position: "relative",
                flexShrink: 0,
                scrollSnapAlign: "center",
                marginRight: "10px",
                marginLeft: "10px",
              }}
              onClick={() => handleStoryClick(story, index)}
            >
              {/* Main Story Circle */}
              <div
                style={{
                  position: "relative",
                  display: "inline-block",
                  marginBottom: "8px",
                }}
              >
                {/* Story Background/Image - Large Circle */}
                <div
                  className="story-circle"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "50%",
                    border: `${borderWidth} ${borderStyle} ${borderColor}`,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    overflow: "hidden",
                    background: story.user?.isAddStory
                      ? "#f9fafb"
                      : story.latestStory?.image || story.image
                        ? `url(${story.latestStory?.image || story.image})`
                        : "#f3f4f6",
                    backgroundSize: "contain",
                    backgroundRepeat: "no-repeat",
                    backgroundPosition: "center",
                    transition: "border-color 0.3s ease",
                  }}
                >
                  {/* Add Story Plus Icon */}
                  {story.user?.isAddStory && (
                    <div
                      style={{
                        color: userTypeColor,
                        fontSize: "32px",
                        fontWeight: "bold",
                        lineHeight: "1",
                      }}
                    >
                      +
                    </div>
                  )}

                  {/* Empty state for stories without images */}
                  {!story.user?.isAddStory && !(story.latestStory?.image || story.image) && (
                    <div
                      style={{
                        width: "90px",
                        height: "90px",
                        borderRadius: "50%",
                        background: "#e5e7eb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          background: "#d1d5db",
                        }}
                      ></div>
                    </div>
                  )}
                </div>

                {/* User Profile Picture - Small Circle at Bottom Center */}
                {!story.user?.isAddStory && (
                  <div
                    className="story-profile"
                    style={{
                      position: "absolute",
                      bottom: "-8px",
                      left: "50%",
                      transform: "translateX(-50%)",
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      border: "3px solid #FEFEFB",
                      overflow: "hidden",
                      background: "#f3f4f6",
                    }}
                  >
                    <img
                      src={story.user?.avatar || getUserPlaceholderImage(story.user)}
                      alt={story.user?.name || "User"}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "contain",
                      }}
                      onError={(e) => {
                        e.target.src = getUserPlaceholderImage(story.user)
                      }}
                    />

                    {/* Story count indicator for grouped stories */}
                    {/* {story.storyCount > 1 && (
                      <div
                        style={{
                          position: "absolute",
                          bottom: "-5px",
                          left: "5px",
                          width: "20px",
                          height: "20px",
                          borderRadius: "50%",
                          backgroundColor: "#2a8ad2",
                          color: "white",
                          fontSize: "10px",
                          fontWeight: "bold",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          border: "2px solid #FEFEFB",
                        }}
                      >
                        {story.storyCount}
                      </div>
                    )} */}
                  </div>
                )}
              </div>
            </div>
            );
          })}
        </div>
      </div>

      {/* Hide scrollbar styles and responsive adjustments */}
      <style>
        {`
          div::-webkit-scrollbar {
            display: none;
          }

          /* Desktop and Tablet Responsive */
          @media (min-width: 769px) {
            .stories-container {
            }

            .stories-scroll-container {
              justify-content: center !important;
              scroll-padding-left: 80px;
            }
          }

          /* Mobile Responsive */
          @media (max-width: 768px) {
            .stories-container {
            }

            .story-item {
              min-width: 80px !important;
              max-width: 80px !important;
            }

            .story-circle {
              width: 80px !important;
              height: 80px !important;
            }

            .story-profile {
              width: 28px !important;
              height: 28px !important;
              bottom: -6px !important;
            }

            .story-name {
              font-size: 12px !important;
              max-width: 80px !important;
            }
          }

          /* Extra small mobile */
          @media (max-width: 480px) {
            .stories-container {
            }

            .story-item {
              min-width: 70px !important;
            }

            .story-circle {
              width: 70px !important;
              height: 70px !important;
            }

            .story-profile {
              width: 24px !important;
              height: 24px !important;
              bottom: -4px !important;
            }

            .add-icon {
              font-size: 24px !important;
            }
          }
        `}
      </style>

      {/* Add Story Modal */}
      <AddStoryModal
        isOpen={isAddStoryModalOpen}
        onClose={handleCloseAddModal}
        onSubmit={handleAddStory}
        currentUser={currentUser}
        isLoading={isUploading}
        uploadProgress={uploadProgress}
      />

      {/* Show Story Modal */}
      <ShowStoryModal
        isOpen={isShowStoryModalOpen}
        onClose={handleCloseShowModal}
        stories={stories}
        initialStoryIndex={selectedStoryIndex}
        currentUser={currentUser}
        onStoryDeleted={onStoryDeleted}
        onStoryViewed={onStoryViewed}
        viewedStoryIds={viewedStoryIds}
      />
    </div>
  )
}

export default StoriesCarousel

